import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../firestore"
import { AdminId } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { assertNever } from "../lib/assert-never"

export const AdminPermissionType = {
  // NGワード閲覧
  ViewNGWord: "ViewNGWord",
  // NGワード編集
  EditNGWord: "EditNGWord",
  // ダッシュボードアクセス
  ViewDashboard: "ViewDashboard",
  // 監査ログ閲覧
  ViewAdminAuditLog: "ViewAdminAuditLog",
  // ユーザーのコイン・サブスクリプション閲覧
  ViewUserCoinAndSubscription: "ViewUserCoinAndSubscription",
  // ユーザーサブスクリプション編集
  EditUserSubscription: "EditUserSubscription",
  // LIVEポイント閲覧
  ReadLivePoints: "ReadLivePoints",
  // LIVEポイント編集（加減算）
  EditLivePoints: "EditLivePoints",
} as const

export type AdminPermissionType = typeof AdminPermissionType[keyof typeof AdminPermissionType]

export const convertAdminPermissionTypeToDisplayString = (permission: AdminPermissionType): string => {
  switch (permission) {
    case "ViewNGWord":
      return "NGワード閲覧"
    case "EditNGWord":
      return "NGワード編集"
    case "ViewDashboard":
      return "ダッシュボード"
    case "ViewAdminAuditLog":
      return "監査ログ閲覧"
    case "ViewUserCoinAndSubscription":
      return "コイン・サブスクリプション閲覧"
    case "EditUserSubscription":
      return "サブスクリプション編集"
    case "ReadLivePoints":
      return "LIVEポイント閲覧"
    case "EditLivePoints":
      return "LIVEポイント編集"
    default:
      return assertNever(permission)
  }
}

const adminPermissionTypeValues: readonly string[] = Object.values(AdminPermissionType)

export function sanitizeAdminPermission(permission: AdminPermission): AdminPermission {
  if (permission.kind === "all") {
    return permission
  }
  return {
    kind: "some",
    values: permission.values.filter((v) => adminPermissionTypeValues.includes(v))
  }
}

export const AdminPermission = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("all"),
  }),
  z.object({
    kind: z.literal("some"),
    values: z.array(z.enum(AdminPermissionType))
  })
])

export type AdminPermission = z.infer<typeof AdminPermission>

export const AdminDocumentMeta = z.object({
  permission: AdminPermission,
  secret_token: z.string().optional().describe("IdToken取得用の認証トークン。"),
})

export type AdminDocumentMeta = z.infer<typeof AdminDocumentMeta>

export const AdminDocumentBody = z.intersection(
  AdminDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt
  })
)

export type AdminDocumentBody = z.infer<typeof AdminDocumentBody>

export type AdminDocument = AdminDocumentBody & {
  id: AdminId
}

export class AdminDocuments {
  static toDocumentData(admin: AdminDocument): object {
    return {
      ...admin,
      // id は document path に入るので入れない
      id: undefined
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): AdminDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: AdminId(doc.id),
      // 過去に作られたAdminは当面の間、全権限保持者として取り扱う
      permission: data["permission"] ?? { kind: "all" },
      secret_token: data["secret_token"],
      created_at: data["created_at"],
      updated_at: data["updated_at"]
    }
  }
}

export const GetAdminWithDetailResponse = z.object({
  id: z.string(),
  permission: AdminPermission,
  createdAt: CreatedAt,
  updatedAt: UpdatedAt,
  email: z.email().optional(),
})

export type GetAdminWithDetailResponse = z.infer<typeof GetAdminWithDetailResponse>

export const AdminUpdateRequest = z.object({
  permission: AdminPermission
})

export type AdminUpdateRequest = z.infer<typeof AdminUpdateRequest>
